import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Pencil, Trash2, Search, Star, Phone, Mail, BookOpen } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Carpentry", "Electrical", "Plumbing", "Painting",
  "Flooring", "Furnishings", "False Ceiling", "Glass & Mirror",
  "Modular Kitchen", "Furniture", "Lighting", "General",
];

const CATEGORY_TONE = {
  Carpentry: "bg-amber-50 text-amber-800",
  Electrical: "bg-yellow-50 text-yellow-800",
  Plumbing: "bg-sky-50 text-sky-800",
  Painting: "bg-rose-50 text-rose-800",
  Flooring: "bg-stone-100 text-stone-800",
  Furnishings: "bg-fuchsia-50 text-fuchsia-800",
  "False Ceiling": "bg-slate-100 text-slate-800",
  "Glass & Mirror": "bg-cyan-50 text-cyan-800",
  "Modular Kitchen": "bg-emerald-50 text-emerald-800",
  Furniture: "bg-teal-50 text-teal-800",
  Lighting: "bg-orange-50 text-orange-800",
  General: "bg-anvi-100 text-anvi-700",
};

function VendorFormModal({ initial, mode, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || "General",
    contact_name: initial?.contact_name || "",
    phone: initial?.phone || "",
    email: initial?.email || "",
    gstin: initial?.gstin || "",
    address: initial?.address || "",
    notes: initial?.notes || "",
    rating: initial?.rating ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, rating: Number(form.rating) };
      const res = isEdit
        ? await api.vendorUpdate(initial.id, payload)
        : await api.vendorCreate(payload);
      toast.success(isEdit ? "Vendor updated" : "Vendor added");
      onSaved(res.data);
      onClose();
    } catch {
      toast.error(isEdit ? "Could not update" : "Could not add vendor");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid={isEdit ? "edit-vendor-modal" : "create-vendor-modal"}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">{isEdit ? "Edit Vendor" : "New Vendor"}</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">{isEdit ? "Update vendor details" : "Add a vendor"}</div>
          </div>
          <button type="button" onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1" data-testid="close-vendor-modal"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Vendor name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-field-name"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="vendor-field-category"
                      className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Contact person</label>
              <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="vendor-field-contact_name"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-field-phone"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-field-email"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">GSTIN</label>
              <input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="vendor-field-gstin"
                     maxLength={15} className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Rating (0–5)</label>
              <input type="number" min="0" max="5" step="0.1" value={form.rating}
                     onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="vendor-field-rating"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Address</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="vendor-field-address"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="vendor-field-notes"
                        className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-vendor-form"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Add vendor")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeleteVendorModal({ vendor, onClose, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.vendorDelete(vendor.id);
      toast.success(`Vendor "${vendor.name}" deleted`);
      onConfirmed(vendor.id);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not delete";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md border border-anvi-200 shadow-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-vendor-modal">
        <div className="text-xs tracking-[0.15em] uppercase text-red-600 mb-1">Danger zone</div>
        <div className="font-heading text-xl text-anvi-900 mb-3">Delete vendor</div>
        <div className="text-sm text-anvi-700 mb-5">Remove <span className="font-medium text-anvi-900">"{vendor.name}"</span> from your vendor master? Blocked if the vendor has open POs.</div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="confirm-delete-vendor"
                  className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 rounded-md disabled:opacity-50">
            {busy ? "Deleting…" : "Delete vendor"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stars({ value }) {
  const rounded = Math.round(value * 2) / 2;
  return (
    <div className="flex items-center gap-0.5 text-amber-500">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={12} strokeWidth={1.5} fill={i <= rounded ? "currentColor" : "none"} />
      ))}
      <span className="text-xs text-anvi-500 ml-1 tabular-nums">{value.toFixed(1)}</span>
    </div>
  );
}

export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const canManage = user && (user.role === "Owner" || user.role === "Admin");

  const load = useCallback(async () => {
    try {
      const r = await api.vendorsList();
      setVendors(r.data || []);
    } catch { toast.error("Could not load vendors"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upsertLocal = (v) => setVendors((prev) => {
    const idx = prev.findIndex((x) => x.id === v.id);
    if (idx === -1) return [v, ...prev];
    const next = [...prev]; next[idx] = { ...next[idx], ...v }; return next;
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vendors.filter((v) => {
      if (filter !== "All" && v.category !== filter) return false;
      if (!q) return true;
      return [v.name, v.contact_name, v.phone, v.email, v.gstin].some((f) => (f || "").toLowerCase().includes(q));
    });
  }, [vendors, query, filter]);

  return (
    <div className="px-10 pt-4 pb-10 animate-fade-in" data-testid="vendors-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">Supply Chain</div>
          <h1 className="font-heading text-4xl text-anvi-900">Vendors</h1>
          <p className="text-sm text-anvi-500 mt-2">{vendors.length} vendor{vendors.length === 1 ? "" : "s"} in your master · used across POs and site execution</p>
        </div>
        <button onClick={() => setCreateOpen(true)} data-testid="new-vendor-btn"
                className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors">
          <Plus size={16} strokeWidth={1.5} /> New Vendor
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-anvi-400" strokeWidth={1.5} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, phone, GSTIN…"
                 data-testid="vendor-search"
                 className="w-full pl-9 pr-3 py-2 border border-anvi-200 rounded-md text-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {["All", ...CATEGORIES].map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`vendor-filter-${c}`}
                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${filter === c ? "bg-anvi-900 text-white border-anvi-900" : "bg-white border-anvi-200 text-anvi-700 hover:border-anvi-terracotta"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((v) => (
          <div key={v.id} data-testid={`vendor-tile-${v.id}`}
               className="group relative bg-white border border-anvi-200 rounded-md p-5 hover:border-anvi-terracotta transition-colors">
            {canManage && (
              <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Link to={`/vendors/${v.id}`} title="Rate card" data-testid={`open-vendor-${v.id}`}
                      className="w-7 h-7 rounded-md bg-white border border-anvi-200 flex items-center justify-center text-anvi-700 hover:text-anvi-terracotta hover:border-anvi-terracotta">
                  <BookOpen size={12} strokeWidth={1.5} />
                </Link>
                <button onClick={() => setEditing(v)} title="Edit" data-testid={`edit-vendor-${v.id}`}
                        className="w-7 h-7 rounded-md bg-white border border-anvi-200 flex items-center justify-center text-anvi-700 hover:text-anvi-terracotta hover:border-anvi-terracotta">
                  <Pencil size={12} strokeWidth={1.5} />
                </button>
                <button onClick={() => setDeleting(v)} title="Delete" data-testid={`delete-vendor-${v.id}`}
                        className="w-7 h-7 rounded-md bg-white border border-anvi-200 flex items-center justify-center text-anvi-700 hover:text-red-600 hover:border-red-500">
                  <Trash2 size={12} strokeWidth={1.5} />
                </button>
              </div>
            )}
            <Link to={`/vendors/${v.id}`} className="block">
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${CATEGORY_TONE[v.category] || "bg-anvi-100 text-anvi-700"}`}>{v.category}</span>
              <div className="font-heading text-lg text-anvi-900 mt-2 pr-24 group-hover:text-anvi-terracotta transition-colors">{v.name}</div>
              {v.contact_name && <div className="text-sm text-anvi-700 mt-0.5">{v.contact_name}</div>}
              <div className="mt-3 space-y-1 text-xs text-anvi-600">
                {v.phone && <div className="flex items-center gap-1.5"><Phone size={11} strokeWidth={1.5} /> {v.phone}</div>}
                {v.email && <div className="flex items-center gap-1.5"><Mail size={11} strokeWidth={1.5} /> {v.email}</div>}
                {v.gstin && <div className="font-mono text-[11px] text-anvi-500">{v.gstin}</div>}
              </div>
              <div className="mt-4 pt-3 border-t border-anvi-100 flex items-center justify-between">
                <Stars value={Number(v.rating) || 0} />
                {v.address && <div className="text-xs text-anvi-500 truncate max-w-[60%]" title={v.address}>{v.address}</div>}
              </div>
            </Link>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-anvi-500 py-16 border border-dashed border-anvi-200 rounded-md" data-testid="vendors-empty">
            {vendors.length === 0 ? "No vendors yet. Click 'New Vendor' to add one." : "No vendors match your filters."}
          </div>
        )}
      </div>

      {createOpen && <VendorFormModal mode="create" onClose={() => setCreateOpen(false)} onSaved={upsertLocal} />}
      {editing && <VendorFormModal mode="edit" initial={editing} onClose={() => setEditing(null)} onSaved={upsertLocal} />}
      {deleting && <ConfirmDeleteVendorModal vendor={deleting} onClose={() => setDeleting(null)} onConfirmed={(id) => setVendors((prev) => prev.filter((x) => x.id !== id))} />}
    </div>
  );
}
